import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { AlertTriangle, Clock, Bell } from 'lucide-react';
import { useInventory } from '../hooks/useInventory';
import { useVisits } from '../../visits/hooks/useVisits';
import { InventoryItem } from '../types/inventory';
import { differenceInDays, formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../components/ui/pagination";

interface AlertsProps {
  mode?: 'all' | 'inventory';
}

type AlertType = 'inventory_expiring' | 'inventory_low' | 'patient_followup' | 'physician_reminder';
type AlertSeverity = 'critical' | 'warning';

interface Alert {
  id: string;
  type: AlertType;
  title: string;
  description: string;
  timestamp: Date;
  severity: AlertSeverity;
  metadata?: any;
}

export function Alerts({ mode = 'inventory' }: AlertsProps) {
  const { data: items, isLoading: isLoadingInventory } = useInventory();
  const { data: visits, isLoading: isLoadingVisits } = useVisits();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getDaysUntilExpiration = (expirationDate: Date | string | undefined): number | null => {
    if (!expirationDate) return null;
    const expDate = new Date(expirationDate);
    return differenceInDays(expDate, new Date());
  };

  // Get inventory alerts
  const inventoryAlerts: Alert[] = [
    ...(items?.filter((item: InventoryItem) => item.quantity <= item.minThreshold).map((item: InventoryItem): Alert => ({
      id: `inventory-low-${item.id}`,
      type: 'inventory_low',
      title: 'Low Stock Alert',
      description: `${item.name} (${item.quantity} ${item.unit} remaining)`,
      timestamp: new Date(),
      severity: 'critical',
      metadata: item
    })) || []),
    ...(items?.filter((item: InventoryItem) => {
      if (!item.expirationDate || !item.minExpirationThreshold) return false;
      const daysUntilExpiration = getDaysUntilExpiration(item.expirationDate);
      return daysUntilExpiration !== null && daysUntilExpiration <= item.minExpirationThreshold;
    }).map((item: InventoryItem): Alert => ({
      id: `inventory-expiring-${item.id}`,
      type: 'inventory_expiring',
      title: 'Expiration Alert',
      description: `${item.name} expires in ${getDaysUntilExpiration(item.expirationDate)} days`,
      timestamp: item.expirationDate ? new Date(item.expirationDate) : new Date(),
      severity: 'warning',
      metadata: item
    })) || [])
  ];

  // Get patient follow-up alerts
  const followUpAlerts: Alert[] = mode === 'all' ? (
    visits?.filter(visit => 
      visit.reason === "Follow-up" && visit.status === "scheduled"
    ).map((visit): Alert => ({
      id: `followup-${visit.id}`,
      type: 'patient_followup',
      title: 'Patient Follow-up Required',
      description: `${visit.patientName || 'Patient'} (${visit.initialNotes || 'Check-up'})`,
      timestamp: new Date(visit.startTime),
      severity: visit.isUrgent ? 'critical' : 'warning',
      metadata: visit
    })) || []
  ) : [];

  // Get physician reminder alerts
  const reminderAlerts: Alert[] = mode === 'all' ? (
    visits?.flatMap(visit => 
      (visit.physicianReminders || [])
        .filter(reminder => new Date(reminder.date) >= new Date())
        .map((reminder): Alert => ({
          id: `reminder-${visit.id}-${reminder.id}`,
          type: 'physician_reminder',
          title: 'Physician Reminder',
          description: `${visit.patientName || 'Patient'} - ${reminder.notes || 'Review Required'}`,
          timestamp: new Date(reminder.date),
          severity: reminder.isUrgent ? 'critical' : 'warning',
          metadata: { visit, reminder }
        }))
    ) || []
  ) : [];

  // Combine and sort all alerts
  const allAlerts = [...inventoryAlerts, ...followUpAlerts, ...reminderAlerts].sort((a, b) => {
    // Sort by severity first (critical before warning)
    if (a.severity !== b.severity) {
      return a.severity === 'critical' ? -1 : 1;
    }
    // Then by timestamp (newest first)
    return b.timestamp.getTime() - a.timestamp.getTime();
  });
  const totalAlerts = allAlerts.length;
  const totalPages = Math.ceil(totalAlerts / itemsPerPage);

  // Get current page items
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAlerts = allAlerts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">
          {mode === 'all' ? 'Urgent Alerts' : 'Inventory Alerts'}
        </h1>
        <Badge variant="destructive" className="px-3 py-1 text-sm">
          {totalAlerts} {totalAlerts === 1 ? 'Alert' : 'Alerts'}
        </Badge>
      </div>

      <div className="space-y-2">
        {currentAlerts.map((alert) => {
          const styles = alert.severity === 'critical'
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
          
          return (
            <div 
              key={alert.id} 
              className={`flex items-center gap-4 p-3 rounded-lg border ${styles}`}
            >
              {/* Icon and Title */}
              <div className="flex items-center gap-3 min-w-[200px]">
              {alert.type === 'inventory_expiring' ? (
                <Clock className="h-5 w-5 text-orange-500 dark:text-orange-300" />
              ) : alert.type === 'patient_followup' || alert.type === 'physician_reminder' ? (
                <Bell className="h-5 w-5 text-orange-500 dark:text-orange-300" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-300" />
              )}
                <div className="flex flex-col">
                  <span className="font-medium">{alert.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(alert.timestamp, { addSuffix: true })}
                  </span>
                </div>
              </div>

              {/* Alert Description */}
              <div className="flex-1">
                <p className="font-medium">{alert.description}</p>
              </div>
            </div>
          );
        })}

        {(isLoadingInventory || (mode === 'all' && isLoadingVisits)) && (
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-muted-foreground">
                Loading alerts...
              </div>
            </CardContent>
          </Card>
        )}
        
        {!isLoadingInventory && !(mode === 'all' && isLoadingVisits) && totalAlerts === 0 && (
          <Card className="bg-green-50 dark:bg-green-900/20 border-0">
            <CardContent className="p-6">
              <div className="text-center text-green-700 dark:text-green-400">
                No alerts at this time
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {totalAlerts > 0 && (
        <div className="mt-6 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => currentPage > 1 ? handlePageChange(currentPage - 1) : undefined}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => handlePageChange(page)}
                    isActive={currentPage === page}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => currentPage < totalPages ? handlePageChange(currentPage + 1) : undefined}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
